import { Shield, Database, Code, Zap } from "lucide-react"

export function MongoDBFeaturesPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Key Features</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          The MongoDB template comes packed with production-ready features including complete 
          authentication, database integration, and developer tools.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <FeatureCard
          icon={<Shield className="h-8 w-8 text-primary" />}
          title="Complete Authentication System"
          items={[
            "JWT access & refresh tokens",
            "Google OAuth 2.0 integration",
            "Role-based access control (USER, ADMIN, SUPER_ADMIN)",
            "Password hashing with bcrypt (12 rounds)",
            "Token refresh with automatic rotation",
            "Device tracking for security",
          ]}
        />
        <FeatureCard
          icon={<Database className="h-8 w-8 text-primary" />}
          title="MongoDB with Mongoose"
          items={[
            "Mongoose ODM integration",
            "Schema-based models with TypeScript",
            "No migrations needed (schemaless)",
            "Automatic schema updates",
            "ObjectId to string transformation",
            "Timestamps on all documents",
          ]}
        />
        <FeatureCard
          icon={<Code className="h-8 w-8 text-primary" />}
          title="Developer Experience"
          items={[
            "TypeScript strict mode",
            "Hot reload with Nest CLI",
            "ESLint + Prettier configured",
            "Swagger & Scalar API docs",
            "Environment-based configuration",
            "Helper scripts included",
          ]}
        />
        <FeatureCard
          icon={<Zap className="h-8 w-8 text-primary" />}
          title="Production Ready"
          items={[
            "Global response wrapper",
            "Global exception filters",
            "Request validation (class-validator)",
            "CORS configuration",
            "Environment validation",
            "Security best practices",
          ]}
        />
      </div>

      <div className="bg-muted/30 rounded-xl p-8 space-y-6 mt-12">
        <h2 className="text-2xl font-bold">Why Choose MongoDB Template?</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Flexibility</h3>
            <p className="text-muted-foreground leading-relaxed">
              MongoDB's schemaless nature allows you to iterate quickly without managing migrations. 
              Perfect for rapid prototyping and evolving data models.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Developer Friendly</h3>
            <p className="text-muted-foreground leading-relaxed">
              Mongoose provides elegant schema validation, middleware, and TypeScript support 
              out of the box.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Scalability</h3>
            <p className="text-muted-foreground leading-relaxed">
              MongoDB's horizontal scaling capabilities make it easy to grow your application 
              as your user base expands.
            </p>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Cloud Ready</h3>
            <p className="text-muted-foreground leading-relaxed">
              Seamless integration with MongoDB Atlas for managed cloud hosting with automatic 
              backups and monitoring.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function FeatureCard({ 
  icon, 
  title, 
  items 
}: { 
  icon: React.ReactNode
  title: string
  items: string[] 
}) {
  return (
    <div className="bg-card border rounded-xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="text-xl font-semibold">{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={index} className="flex gap-3 text-sm text-muted-foreground">
            <span className="text-primary shrink-0">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

